import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DownloadIcon from '@mui/icons-material/Download';
import { getToken } from '../services/authApi';

/**
 * VisorDocumentoSeguro
 *
 * Carga la imagen/PDF a través de fetch() con el token de auth del admin,
 * crea un blob URL efímero en memoria y lo destruye al cerrar.
 * La URL real NUNCA aparece en el DOM, la barra del navegador ni en Network.
 *
 * Props:
 *   open         {boolean}  — controla si el diálogo está abierto
 *   onClose      {function} — callback al cerrar
 *   urlDocumento {string}   — URL real del documento (Firebase Storage, etc.)
 *   titulo       {string}   — título en el encabezado del visor
 *   nombreAfiliado {string} — nombre del afiliado (solo para el tooltip de descarga)
 *   permitirDescarga {boolean} — si false, oculta el botón de descarga (default: false)
 *
 * Uso:
 *   <VisorDocumentoSeguro
 *     open={verDocumento}
 *     onClose={() => setVerDocumento(false)}
 *     urlDocumento={afiliado.UrlDocIdentidad}
 *     titulo="Documento de identidad"
 *     nombreAfiliado={afiliado.Nombres}
 *   />
 */
export default function VisorDocumentoSeguro({
  open,
  onClose,
  urlDocumento,
  titulo = 'Documento',
  nombreAfiliado = '',
  permitirDescarga = false,
}) {
  const [blobUrl, setBlobUrl]         = useState(null);
  const [cargando, setCargando]       = useState(false);
  const [error, setError]             = useState(null);
  const [zoom, setZoom]               = useState(1);
  const [rotacion, setRotacion]       = useState(0);
  const [esPdf, setEsPdf]             = useState(false);
  const blobRef                        = useRef(null);  // guardamos ref para revoke seguro

  // ── Carga segura ──────────────────────────────────────────────────────────
  const cargarDocumento = useCallback(async () => {
    if (!urlDocumento) return;

    setCargando(true);
    setError(null);
    setBlobUrl(null);
    setZoom(1);
    setRotacion(0);

    try {
      // El token JWT viene incluido en el objeto que retorna getUrlFirmadaDocumento
      const token = getToken();

      const headers = { 'Authorization': `Bearer ${token}` };

      // 2. Fetch del recurso — la URL queda solo en el proceso JS, nunca en el DOM
      const respuesta = await fetch(urlDocumento, { headers });

      if (!respuesta.ok) {
        throw new Error(`Error al cargar el documento (${respuesta.status})`);
      }

      // 3. Detectar si es PDF por Content-Type
      const contentType = respuesta.headers.get('Content-Type') || '';
      const isPdf = contentType.includes('pdf');
      setEsPdf(isPdf);

      // 4. Convertir a blob y crear URL temporal en memoria
      const blob = await respuesta.blob();
      const url  = URL.createObjectURL(blob);

      // 5. Si había un blob anterior, liberarlo
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
      }
      blobRef.current = url;
      setBlobUrl(url);

    } catch (err) {
      console.error('[VisorDocumentoSeguro] Error al cargar:', err);
      setError(err.message || 'No se pudo cargar el documento.');
    } finally {
      setCargando(false);
    }
  }, [urlDocumento]);

  // ── Efectos ───────────────────────────────────────────────────────────────

  // Cargar cuando el diálogo se abre y hay URL
  useEffect(() => {
    if (open && urlDocumento) {
      cargarDocumento();
    }
  }, [open, urlDocumento, cargarDocumento]);

  // Limpiar el blob URL al cerrar para que no quede accesible en memoria
  useEffect(() => {
    if (!open && blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
      setBlobUrl(null);
    }
  }, [open]);

  // Limpiar al desmontar el componente
  useEffect(() => {
    return () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
  }, []);

  // ── Handlers de zoom y rotación ───────────────────────────────────────────
  const aumentarZoom  = () => setZoom(z => Math.min(z + 0.25, 4));
  const reducirZoom   = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const rotar         = () => setRotacion(r => (r + 90) % 360);

  // ── Descarga segura (si está permitido) ───────────────────────────────────
  // Descarga desde el blob en memoria — el nombre de archivo no revela la URL
  const descargarDocumento = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    const extension = esPdf ? 'pdf' : 'jpg';
    const nombreArchivo = nombreAfiliado
      ? `doc_${nombreAfiliado.replace(/\s+/g, '_').toLowerCase()}.${extension}`
      : `documento.${extension}`;
    a.download = nombreArchivo;
    a.click();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#2e7d32',
          color: '#fff',
          py: 1.5,
          px: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          {titulo}
          {nombreAfiliado && (
            <Typography component="span" variant="body2" sx={{ ml: 1, opacity: 0.8 }}>
              — {nombreAfiliado}
            </Typography>
          )}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ── Barra de controles ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          bgcolor: '#e8f5e9',
          borderBottom: '1px solid #c8e6c9',
        }}
      >
        <Tooltip title="Reducir zoom">
          <span>
            <IconButton size="small" onClick={reducirZoom} disabled={zoom <= 0.5 || cargando}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center', color: 'text.secondary' }}>
          {Math.round(zoom * 100)}%
        </Typography>

        <Tooltip title="Aumentar zoom">
          <span>
            <IconButton size="small" onClick={aumentarZoom} disabled={zoom >= 4 || cargando}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {!esPdf && (
          <Tooltip title="Rotar">
            <span>
              <IconButton size="small" onClick={rotar} disabled={cargando}>
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {permitirDescarga && blobUrl && (
          <Tooltip title="Descargar (sin exponer URL)">
            <IconButton size="small" onClick={descargarDocumento} sx={{ color: '#2e7d32' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Contenido principal ── */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          maxHeight: '70vh',
          overflow: 'auto',
        }}
      >
        {/* Estado: cargando */}
        {cargando && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} sx={{ color: '#2e7d32' }} />
            <Typography variant="body2" color="text.secondary">
              Cargando documento de forma segura…
            </Typography>
          </Box>
        )}

        {/* Estado: error */}
        {!cargando && error && (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="error" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={cargarDocumento}
              sx={{ mt: 1, color: '#2e7d32', borderColor: '#2e7d32' }}
            >
              Reintentar
            </Button>
          </Box>
        )}

        {/* Estado: documento listo — imagen */}
        {!cargando && !error && blobUrl && !esPdf && (
          <Box
            sx={{
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              minHeight: 400,
            }}
          >
            <img
              src={blobUrl}
              alt="Documento de identidad"
              style={{
                transform: `scale(${zoom}) rotate(${rotacion}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
                maxWidth: zoom <= 1 ? '100%' : 'none',
                display: 'block',
                // Bloqueamos el menú contextual y el drag
                pointerEvents: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              // Bloqueamos clic derecho → "Abrir imagen en pestaña nueva"
              onContextMenu={e => e.preventDefault()}
              draggable={false}
            />
          </Box>
        )}

        {/* Estado: documento listo — PDF */}
        {!cargando && !error && blobUrl && esPdf && (
          <Box sx={{ width: '100%', height: '65vh' }}>
            <iframe
              src={blobUrl}
              title="Documento PDF"
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
          Visualización segura — el documento no es accesible fuera de esta sesión.
        </Typography>
        <Button onClick={onClose} variant="contained" size="small" sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
